import { Component, EventEmitter, Inject, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { Store } from '@ngrx/store';
import { filter, find, map, set, sortBy } from 'lodash';
import { SubscriptionLike as ISubscription } from 'rxjs';
import { DateHelpers } from 'Src/ng2/shared/services/date-helpers/date-helpers.service';
import { TValidGradDates } from '../../../shared/constants/graduation-date.constant';
import { GraduationPlanTransfer } from '../../../shared/constants/graduation-plan-transfer.constant';
import { unsubscribeComponent } from '../../../shared/helpers/unsubscribe-decorator/unsubscribe-decorators.helper';
import { ModalsService } from '../../../shared/modals/modals.service';
import { ImStudent } from '../../../shared/services/im-models/im-student.service';
import { ISchool } from '../../../shared/typings/interfaces/school.interface';
import { IStudent, TValidPlannedDiplomaTypes } from '../../../shared/typings/interfaces/student.interface';
import { getStudentList } from '../../../store/selectors/student-selector';
import { StudentPanelService } from '../student-panel.service';
import { PlannedDiplomaOptionsModal } from './../../../../app/shared/modals/planned-diploma-options/planned-diploma-options.service';
import {
  GraduationDate,
  IGraduationDate,
  NextGraduationDate,
} from './../../../shared/constants/graduation-date.constant';
import { GraduationPlan } from './../../../shared/constants/graduation-plan.constant';
import { ISummaryItem } from './../student-panel.component';
import GradV3Config from './student-grad-panel-v3.config';

interface ITableRow {
  field: string;
  value: string;
  fullVal?: string; // allows for styling in dropdowns for truncated values
  display: boolean;
  editable: boolean;
  options?: string[];
}

export const PLANNED_DIPLOMA_TYPES = ['Regents', 'Advanced Regents', 'Local', 'Non-Graduate', 'No plan'];

@Component({
  selector: 'student-grad-panel-v3',
  template: require('./student-grad-panel-v3.component.html'),
  styles: [require('./student-grad-panel-v3.scss')],
  encapsulation: ViewEncapsulation.None,
})
@unsubscribeComponent
export class StudentGradPanelV3 implements OnInit {
  @Input() student: IStudent;
  @Input() school: ISchool;
  @Input() collapsed: boolean;
  @Output() saveStudent: EventEmitter<any> = new EventEmitter();
  @Input() isRestricted: boolean;

  private panelTitle: string;
  table1: ITableRow[];
  table2: ITableRow[];
  summaryItems: ISummaryItem[];

  // subscriptions
  openAlertModalSubscription: ISubscription;

  constructor(
    private studentPanel: StudentPanelService,
    private modalService: ModalsService,
    private store: Store<any>,
    @Inject('PlannedDiplomaOptionsModal') private plannedDiplomaOptionsModal: PlannedDiplomaOptionsModal,
    private imStudent: ImStudent,
    @Inject('ImStudentGradPlanning') private imStudentGradPlanning,
    private dateHelpers: DateHelpers,
  ) {}

  ngOnInit() {
    this.panelTitle = GradV3Config.title;
    this.collapsed = this.studentPanel.getInitialPanelState('student-grad-panel-v3');
    this.renderTables();
  }

  // Returns true if grad plan is incomplete or in the past
  // TODO: add corresponding test
  gradPlanIsIncompleteOrInPast(): boolean {
    let gradPlanIsIncompleteOrInPast = false;
    const isTransferStudent = this.imStudent.isTransferStudent(this.student, this.school);

    if (isTransferStudent) {
      const gradPlan = this.imStudent.getCurrentGradPlanTransfer(this.student);
      const isInPast = gradPlan === GraduationPlanTransfer.PLAN_IN_PAST.humanName;
      const isIncomplete = gradPlan === GraduationPlanTransfer.PLAN_INCOMPLETE.humanName;
      gradPlanIsIncompleteOrInPast = isInPast || isIncomplete;
    } else {
      const gradPlan = this.imStudent.getCurrentGradPlan(this.student);
      const isInPast = gradPlan === GraduationPlan.PLAN_IN_PAST.humanName;
      const isIncomplete = gradPlan === GraduationPlan.PLAN_INCOMPLETE.humanName;
      gradPlanIsIncompleteOrInPast = isInPast || isIncomplete;
    }
    return gradPlanIsIncompleteOrInPast;
  }

  renderTables(): void {
    this.summaryItems = [];
    const {
      gradPlanningDetails: {
        plannedDiplomaType,
        plannedGraduationDate,
        schoolVerifiedSafetyNetEligibility,
        plannedEndorsements,
        plannedCreds,
      },
    } = this.student;

    const gradDateOptions = this.filterGradDates();

    this.table1 = [
      {
        field: 'Planned diploma type',
        value: this.truncate(plannedDiplomaType) || `—`,
        fullVal: plannedDiplomaType || `—`,
        options: PLANNED_DIPLOMA_TYPES,
        display: true,
        editable: !this.isRestricted,
      },
      {
        field: 'Planned grad date',
        value: plannedGraduationDate || `—`,
        options: gradDateOptions,
        display: true,
        editable: !this.isRestricted,
      },
      {
        field: 'School indicated safety net eligible',
        value: this.parseSafetyNetEligible(schoolVerifiedSafetyNetEligibility),
        options: ['Yes', 'No', `—`],
        display: true,
        editable: !this.isRestricted,
      },
    ];
    const endorsementsString =
      plannedEndorsements && plannedEndorsements.length > 0 ? plannedEndorsements.join(', ') : `—`;
    const credsString = plannedCreds && plannedCreds.length > 0 ? plannedCreds.join(', ') : `—`;

    this.table2 = [
      {
        field: 'Endorsements',
        value: endorsementsString,
        display: true,
        editable: !this.isRestricted,
      },
      {
        field: 'Credentials',
        value: credsString,
        display: true,
        editable: !this.isRestricted,
      },
    ];
    const _gradPlanIsIncompleteOrInPast = this.gradPlanIsIncompleteOrInPast();
    if (_gradPlanIsIncompleteOrInPast) {
      this.summaryItems.push({
        icon: 'flag',
        text: `INCOMPLETE GRADUATION PLAN`,
      });
    }
  }

  ngOnChanges(changes): void {
    if (changes.student) this.renderTables();
  }

  updateDiplomaType(value: TValidPlannedDiplomaTypes): void {
    const path = 'gradPlanningDetails.plannedDiplomaType';
    const patch = set({}, path, value);
    const validSafetyNetEligibility = this.imStudentGradPlanning.hasValidSafetyNetEligibility(
      this.student,
      patch,
      value,
    );

    if (validSafetyNetEligibility) {
      this.saveStudent.emit({ patch, path });
    } else {
      this._showInvalidPlannedDiplomaTypeErrModal(value);
    }
  }

  updateGradDate(value: TValidGradDates): void {
    const path = 'gradPlanningDetails.plannedGraduationDate';
    const parsedValue = value === '—' ? null : value;
    const patch = set({}, path, parsedValue);
    this.saveStudent.emit({ patch, path });
  }

  updateSafteyNetEligibility(value: string): void {
    const path = 'gradPlanningDetails.schoolVerifiedSafetyNetEligibility';
    const bool = this.parseSafetyNetEligibleBoolean(value);
    const patch = set({}, path, bool);
    this.saveStudent.emit({ patch, path });
  }

  onChangeHandler(field: string, option: string) {
    switch (field) {
      case 'Planned diploma type':
        this.updateDiplomaType(option as TValidPlannedDiplomaTypes);
        this.table1[0].value = this.truncate(option);
        break;
      case 'Planned grad date':
        this.updateGradDate(option as TValidGradDates);
        this.table1[1].value = option;
        break;
      case 'School indicated safety net eligible':
        this.updateSafteyNetEligibility(option);
        this.table1[2].value = option;
        break;
      default:
        break;
    }
  }

  truncate(text): string {
    if (!text) return null;
    return text.length > 8 ? `${text.substring(0, 8)}...` : text;
  }

  isOptionActive(field: string, option: string, table: ITableRow[], fullVal?: string): boolean {
    let row = find(table, (tableRow: ITableRow) => {
      return tableRow.field === field;
    });
    if (row.value === null) return option === null;
    if (fullVal) {
      return this.truncate(row.value) === this.truncate(option);
    } else {
      return row.value === option;
    }
  }

  togglePanelBody(): void {
    this.collapsed = this.studentPanel.togglePanelBody(this.collapsed, this.panelTitle);
  }

  parseSafetyNetEligible(status: boolean): string {
    switch (status) {
      case true:
        return 'Yes';
      case false:
        return 'No';
      default:
        return '—';
    }
  }

  parseSafetyNetEligibleBoolean(status: string): boolean {
    switch (status) {
      case 'Yes':
        return true;
      case 'No':
        return false;
      default:
        return null;
    }
  }

  filterGradDates() {
    const gradDateHumanNames = map(GraduationDate, (date: IGraduationDate) => {
      return date.humanName;
    });

    const sortedDates = sortBy(gradDateHumanNames, (date: string) => {
      return new Date(date);
    });

    const filteredDates = filter(sortedDates, date => {
      const now = this.dateHelpers.getMomentObjForMonthYearDate(NextGraduationDate.humanName).startOf('month');
      const _date = this.dateHelpers.getMomentObjForMonthYearDate(date).startOf('month');
      return _date.isSameOrAfter(now);
    });

    return [...filteredDates, '—'];
  }

  openEndorsementsCreditsModal() {
    const { _id } = this.student;
    this.plannedDiplomaOptionsModal.show([_id], getStudentList).then(res => {
      const { newEndorsements, newCredentials } = res;
      this.table2[0].value = newEndorsements.join(', ');
      this.table2[1].value = newCredentials.join(', ');
    });
  }

  isSafetyNetRow(field: string): boolean {
    return field === 'School indicated safety net eligible';
  }

  _showInvalidPlannedDiplomaTypeErrModal(plannedDiplomaType = this.student.gradPlanningDetails.plannedDiplomaType) {
    const title = 'Invalid Diploma Type';
    const message =
      `Invalid diploma type "${plannedDiplomaType}". ` +
      'Student must be "SPED" or have "School Verified Safety Net Eligibility" ' +
      `to be eligible for a "${plannedDiplomaType}" diploma type.`;

    this.openAlertModalSubscription = this.modalService
      .openAlertModal({
        title,
        message,
      })
      .afterClosed()
      .subscribe(() => {
        // reset dropdown to original value (CM).
        this.table1[0].value = this.truncate(this.student.gradPlanningDetails.plannedDiplomaType);
      });
  }
}
